import React from "react";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/delivery.scss";
import PolicySidebar from "./PolicySidebar";

function Delivery() {
  const complimentaryDeliveryAvailable = Number.isFinite(
    siteConfig.shipping.freeThreshold
  );

  return (
    <main className="policy-page policy-page--shipping">
      <header className="policy-hero">
        <div>
          <p className="policy-eyebrow">Customer care · {siteConfig.brandName}</p>
          <h1>Shipping &amp; delivery</h1>
        </div>
        <div className="policy-hero__intro">
          <p>
            A clear guide to dispatch times, estimated delivery and what happens
            after your order leaves the studio.
          </p>
          <span>Last updated 17 October 2026</span>
        </div>
      </header>

      <div className="policy-shell">
        <PolicySidebar
          activePath="/shipping"
          tableOfContents={[
            { id: "shipping-dispatch", label: "Dispatch" },
            { id: "shipping-rates", label: "Rates & estimates" },
            { id: "shipping-tracking", label: "Tracking" },
            { id: "shipping-international", label: "International orders" },
            { id: "shipping-problems", label: "Delivery problems" },
          ]}
        />

        <article className="policy-content">
          <div className="policy-demo-note" role="note">
            <strong>Before ordering</strong>
            <p>
              Checkout confirms whether a destination is supported and shows the
              delivery charge collected by the store. Transit dates are estimates,
              not guaranteed arrival appointments.
            </p>
          </div>

          <section id="shipping-dispatch">
            <p className="policy-section-number">01</p>
            <h2>Preparing your order</h2>
            <p>
              Orders are prepared during studio business hours. Our current
              dispatch window is <strong>{siteConfig.shipping.dispatchDays}</strong>.
              Weekends and public holidays begin processing on the next business day.
            </p>
            <p>
              Once an order is packed, we send a
              dispatch email with the carrier and tracking reference. Delivery
              estimates begin from dispatch, not from the moment an order is placed.
            </p>
          </section>

          <section id="shipping-rates">
            <p className="policy-section-number">02</p>
            <h2>Rates &amp; delivery estimates</h2>
            <p>
              Final services depend on destination, parcel size and carrier
              availability. The checkout screen should always be treated as the
              final delivery rate for the order.
            </p>
            <div className="policy-cards policy-cards--three">
              <div className="policy-card">
                <span>Standard</span>
                <strong>{formatPrice(siteConfig.shipping.standardRate)}</strong>
                <p>Tracked service for eligible destinations shown at checkout.</p>
              </div>
              <div className="policy-card">
                <span>Complimentary</span>
                <strong>
                  {complimentaryDeliveryAvailable
                    ? `From ${formatPrice(siteConfig.shipping.freeThreshold)}`
                    : "Not currently offered"}
                </strong>
                <p>
                  {complimentaryDeliveryAvailable
                    ? "Applied to qualifying baskets before optional surcharges."
                    : "Any temporary free-delivery offer will state its own conditions."}
                </p>
              </div>
              <div className="policy-card">
                <span>Oversized &amp; fragile</span>
                <strong>Quoted at checkout</strong>
                <p>Extra protection or a specialist carrier may be required.</p>
              </div>
            </div>
            <p className="policy-small-print">
              Estimated transit: Hong Kong 2–4 business days; most supported Asian
              destinations 3–7 business days; other supported destinations 5–12 business days. These
              are estimates, not guarantees.
            </p>
          </section>

          <section id="shipping-tracking">
            <p className="policy-section-number">03</p>
            <h2>Tracking your parcel</h2>
            <p>
              Tracking may take up to 24 hours to update after a label is created.
              During busy periods, carrier scans can appear later than the physical
              movement of a parcel.
            </p>
            <ul>
              <li>Check the delivery address before completing checkout.</li>
              <li>Use the carrier link supplied in the dispatch email.</li>
              <li>Allow one full business day after the estimated delivery date.</li>
            </ul>
          </section>

          <section id="shipping-international">
            <p className="policy-section-number">04</p>
            <h2>International orders</h2>
            <p>
              Local taxes or destination handling fees may apply where required.
              Only charges shown in the checkout total are collected by the store.
              Customs authorities or carriers may collect import duties, local taxes or
              handling fees separately. Carrier delays remain outside our direct control.
            </p>
            <p>
              Some fragile or restricted objects may not be available in every
              destination. Checkout should prevent unsupported services before payment.
            </p>
          </section>

          <section id="shipping-problems">
            <p className="policy-section-number">05</p>
            <h2>Delays, damage or a missing parcel</h2>
            <p>
              If an order arrives damaged, keep the packaging and photograph
              both the parcel and item. For a delayed parcel, contact the team with the
              order reference after checking the latest carrier scan.
            </p>
            <div className="policy-contact-box">
              <div>
                <span>Need a hand?</span>
                <strong>{siteConfig.contact.hours}</strong>
              </div>
              <a href={`mailto:${siteConfig.contact.email}`}>Contact customer care</a>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
